import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { NewMeetingModel } from "services/api/cplapi/CPLAPI";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import "./MeetingForm.scss";
import { Permission } from "services/userSession/UserPermittedComponent";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useAutosaveValue from "app/modules/shared/autosave/useAutosaveValue";
import {
  createMeeting,
  resetData,
  selectCreatedMeetingID,
  selectDeletingMeeting,
  selectEditingMeeting,
  selectLoadingMeeting,
  selectMeeting,
} from "./MeetingFormSlice";
import { MeetingAttendeeTypes } from "global/enums";
import { ClearUnusedAttendees } from "./MeetingFormUtils";
import MeetingForm from "./MeetingForm";

const AUTOSAVE_FORM_KEY = "NewMeeting";

const NewMeetingForm: React.FC = () => {
  const loadingMeeting = useSelector(selectLoadingMeeting);
  const editingMeeting = useSelector(selectEditingMeeting);
  const deletingMeeting = useSelector(selectDeletingMeeting);
  const createdMeetingID = useSelector(selectCreatedMeetingID);
  const existingMeeting = useSelector(selectMeeting);
  const [meetingAttendeeType, setMeetingAttendeeType] = useState(MeetingAttendeeTypes.Development);

  const readonly = loadingMeeting || editingMeeting || deletingMeeting;

  const [autosavedValues, clearAutosavedValues] = useAutosaveValue<NewMeetingModel>(
    AUTOSAVE_FORM_KEY,
    new NewMeetingModel({ title: "", meetingDate: new Date() }),
    (savedValue) =>
      new NewMeetingModel({
        ...savedValue,
        meetingDate:
          savedValue.meetingDate === undefined || savedValue.meetingDate < new Date()
            ? new Date()
            : savedValue.meetingDate,
      })
  );

  const thunkDispatch = useThunkDispatch();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    if (createdMeetingID !== 0 && existingMeeting?.meetingID === createdMeetingID) {
      clearAutosavedValues();
      dispatch(resetData());
      navigate(`/Meeting/${existingMeeting?.meetingID}/Details`);
    }
  }, [dispatch, navigate, clearAutosavedValues, createdMeetingID, existingMeeting?.meetingID]);

  const handleCreate = (values: NewMeetingModel) => {
    ClearUnusedAttendees(meetingAttendeeType, values);
    thunkDispatch(createMeeting(values));
  };

  const getFormButtons = () => (
    <>
      <Permission permissionPredicate={(permission) => permission === Permissions.CreateMeetings}>
        <button data-test-id="meeting-form__create-button" className="btn btn-success meeting-button" type="submit">
          Create
        </button>
      </Permission>
      <button
        data-test-id="meeting-form__cancel-button"
        className="btn btn-secondary meeting-button"
        type="button"
        onClick={() => {
          clearAutosavedValues();
          navigate("/Meetings");
        }}
      >
        Cancel
      </button>
    </>
  );

  return (
    <ActivityIndicator isBusy={loadingMeeting || editingMeeting}>
      <div className="card mb-6">
        <div className="card-header">
          <div className="card-title">New Meeting</div>
          <div className="card-toolbar"></div>
        </div>
        <div className="card-body">
          <MeetingForm
            getFormButtons={getFormButtons}
            handleSubmit={handleCreate}
            initialValues={autosavedValues}
            readonly={readonly}
            meetingAttendeeType={meetingAttendeeType}
            setMeetingAttendeeType={setMeetingAttendeeType}
            AUTOSAVE_FORM_KEY={AUTOSAVE_FORM_KEY}
          />
        </div>
      </div>
    </ActivityIndicator>
  );
};

export default NewMeetingForm;
